// Today (the sales pipeline's front page): the follow-ups that are due — overdue ones first, in red — and the new leads worth
// contacting, best prospects first, as compact rows with the actions to do it from right there: message, snooze, open. A strip of
// figures on top says how the day stands. The api gives the due and new lists and the first messages left today; it does not
// count what was contacted this week, so that is not shown.

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, EmptyState, Menu, Skeleton, StatCard, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { useNow } from '../../floor/live';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { Section, SummaryStrip } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_LEADS } from '../sections';
import { LeadPriorityPill, ratingText } from './labels';
import { LeadsHeader } from './LeadsHeader';
import { MessageDialog } from './MessageDialog';
import { leadParams, useLeadQueue, useUpdateLead, type LeadSummary } from './queries';
import { followUpIn, startOfLocalDay } from './when';
import '../hq.css';

export function TodayPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_LEADS}>
      <Today />
    </RequirePlatformPermission>
  );
}

function Today(): ReactNode {
  const { t } = useTranslation('hq');
  const queue = useLeadQueue();
  const now = useNow(60_000);
  const [messaging, setMessaging] = useState<string | undefined>(undefined);

  let body: ReactNode;
  if (queue.data === undefined) {
    body = queue.isError ? (
      <LoadFailure error={queue.error} message={t('leads.today.unavailable')} onRetry={() => void queue.refetch()} />
    ) : (
      <div aria-busy="true" className="console-lead-today__skeleton">
        <Skeleton height="4rem" />
        <Skeleton height="12rem" />
        <Skeleton height="12rem" />
      </div>
    );
  } else {
    const { due, fresh, firstMessagesLeftToday, dailyCap } = queue.data;
    const dayStart = startOfLocalDay(now);
    const overdue = due.filter((lead) => lead.nextFollowUpAt !== null && new Date(lead.nextFollowUpAt).getTime() < dayStart).length;
    body =
      due.length === 0 && fresh.length === 0 ? (
        <EmptyState
          icon={<NavIcon name="leads" />}
          title={t('leads.today.emptyAll')}
          description={t('leads.today.emptyAllHint')}
          action={
            <Button asChild variant="primary">
              <Link to={HQ_PATHS.pipeline}>{t('leads.today.goPipeline')}</Link>
            </Button>
          }
        />
      ) : (
        <>
          <SummaryStrip label={t('leads.today.title')}>
            <StatCard label={t('leads.today.stats.dueToday')} value={due.length - overdue} />
            <StatCard label={t('leads.today.stats.overdue')} value={overdue} tone={overdue > 0 ? 'danger' : 'default'} />
            <StatCard label={t('leads.today.stats.firstLeft')} value={firstMessagesLeftToday} unit={t('leads.today.stats.of', { cap: dailyCap })} tone={firstMessagesLeftToday === 0 ? 'warn' : 'default'} />
          </SummaryStrip>
          <Section title={t('leads.today.due.title')} description={t('leads.today.due.hint')} body="rows">
            <LeadRows label={t('leads.today.listLabelDue')} leads={due} empty={t('leads.today.due.empty')} snooze onMessage={setMessaging} now={now} />
          </Section>
          <Section title={t('leads.today.fresh.title')} description={t('leads.today.fresh.hint')} body="rows">
            <LeadRows label={t('leads.today.listLabelFresh')} leads={fresh} empty={t('leads.today.fresh.empty')} onMessage={setMessaging} now={now} />
          </Section>
        </>
      );
  }

  return (
    <LeadsHeader current="today">
      {body}
      <MessageDialog leadId={messaging} onClose={() => setMessaging(undefined)} />
    </LeadsHeader>
  );
}

function LeadRows({ label, leads, empty, snooze = false, onMessage, now }: { readonly label: string; readonly leads: readonly LeadSummary[]; readonly empty: string; readonly snooze?: boolean; readonly onMessage: (leadId: string) => void; readonly now: number }): ReactNode {
  if (leads.length === 0) return <p className="console-subtle console-lead-rows__empty">{empty}</p>;
  return (
    <ul className="console-lead-rows" aria-label={label}>
      {leads.map((lead) => (
        <LeadRow key={lead.id} lead={lead} snooze={snooze} onMessage={onMessage} now={now} />
      ))}
    </ul>
  );
}

function LeadRow({ lead, snooze, onMessage, now }: { readonly lead: LeadSummary; readonly snooze: boolean; readonly onMessage: (leadId: string) => void; readonly now: number }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const problems = useProblem();
  const { show } = useToast();
  const update = useUpdateLead();
  const place = [lead.city, lead.area].filter((part): part is string => part !== null && part !== '').join(' · ');
  const dayStart = startOfLocalDay(now);
  const due = lead.nextFollowUpAt === null ? undefined : new Date(lead.nextFollowUpAt).getTime();
  const overdue = due !== undefined && due < dayStart;

  const postpone = (days: number) => {
    const at = followUpIn(days, Date.now());
    update.mutate(
      { leadId: lead.id, body: { nextFollowUpAt: at } },
      {
        onSuccess: () => show({ tone: 'ok', title: t('leads.today.snoozed', { date: format.date(at, 'medium') }) }),
        onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.today.snoozeFailed') }) }),
      },
    );
  };

  return (
    <li className="console-lead-row">
      <div className="console-lead-row__main">
        <Link to={HQ_PATHS.lead} params={leadParams(lead.id)} className="console-lead-row__name">
          {lead.businessName}
        </Link>
        {place === '' ? null : <span className="console-hq-secondary">{place}</span>}
      </div>
      <div className="console-lead-row__facts">
        <LeadPriorityPill priority={lead.priority} />
        {lead.googleRatingTenths === null ? null : (
          <span className="console-num">
            {ratingText(lead.googleRatingTenths)}
            {lead.googleReviewCount === null ? null : <span className="console-subtle"> ({t('leads.pipeline.reviews', { count: lead.googleReviewCount })})</span>}
          </span>
        )}
        <span className="console-hq-secondary">{lead.lastContactedAt === null ? t('leads.today.neverContacted') : t('leads.today.lastContact', { when: format.relative(lead.lastContactedAt) })}</span>
        {lead.nextFollowUpAt === null ? null : (
          <span className={overdue ? 'console-lead-card__followup--overdue' : 'console-hq-secondary'}>{t(overdue ? 'leads.today.overdueWhen' : 'leads.today.dueWhen', { when: format.relative(lead.nextFollowUpAt) })}</span>
        )}
      </div>
      <div className="console-lead-row__actions">
        <Button size="sm" variant="primary" onClick={() => onMessage(lead.id)}>
          {t('leads.today.message')}
        </Button>
        {snooze ? (
          <Menu
            items={[
              { id: 'tomorrow', label: t('leads.today.snoozeTomorrow'), onSelect: () => postpone(1) },
              { id: 'in3', label: t('leads.today.snooze3'), onSelect: () => postpone(3) },
            ]}
            trigger={
              <Button size="sm" variant="secondary" aria-label={`${t('leads.today.snooze')}: ${lead.businessName}`}>
                {t('leads.today.snooze')}
              </Button>
            }
          />
        ) : null}
        <Button asChild size="sm" variant="ghost">
          <Link to={HQ_PATHS.lead} params={leadParams(lead.id)} aria-label={`${t('leads.today.open')}: ${lead.businessName}`}>
            {t('leads.today.open')}
          </Link>
        </Button>
      </div>
    </li>
  );
}
